import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import axios, { AxiosResponse } from 'axios'
import { z } from 'zod'

const REASONING_VALUES = {
  'No selection': 0,
  'Based on evidence': 1,
  'Based on program rules': 2,
  Other: 3,
} as const

type ReasoningString = keyof typeof REASONING_VALUES

export const ReasoningInputSchema = z.enum(
  ['No selection', 'Based on evidence', 'Based on program rules', 'Other'],
  {
    errorMap: () => ({ message: 'Please select a valid reasoning.' }),
  }
)

export type ReasoningInput = z.infer<typeof ReasoningInputSchema>

export const ReasoningSchema = ReasoningInputSchema.transform((val) => {
  return REASONING_VALUES[val]
})

const DecisionSchema = z.enum(['accepted', 'rejected'], {
  message: 'Please select a decision.',
})
type Decision = z.infer<typeof DecisionSchema>

const decisionToApiValue = {
  accepted: 1,
  rejected: 2,
} as const

const PayloadSchema = z.object({
  decision: DecisionSchema,
  flagId: z.number(),
  justification: z.string(),
  reasoning: ReasoningInputSchema,
})

const APIPayloadSchema = z.object({
  flagId: z.number(),
  status: z.literal(1).or(z.literal(2)),
  justification: z.string(),
  reasoning: z.number(),
})

type Payload = z.infer<typeof PayloadSchema>
type APIPayload = z.infer<typeof APIPayloadSchema>
type ChatPayload = {
  flag_id: number
  message: string
}

function transformPayload(payload: Payload): APIPayload {
  return {
    flagId: payload.flagId,
    status: decisionToApiValue[payload.decision],
    justification: payload.justification,
    reasoning: REASONING_VALUES[payload.reasoning],
  }
}

function updateDecision(payload: APIPayload) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return axios.put<any, AxiosResponse<{ success: boolean; message: string }>>(
    `/application_flags_update/${payload.flagId}`,
    payload
  )
}

function sendChatMessage(payload: ChatPayload) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return axios.post<any, AxiosResponse<{ success: boolean; message: string }>>(
    `/flags/messages`,
    payload
  )
}

type SubmitFlagDecisionOptions = Omit<
  UseMutationOptions<
    AxiosResponse<{ success: boolean; message: string }>,
    Error,
    Payload,
    unknown
  >,
  'mutationFn'
>

function useSubmitFlagDecision(options?: SubmitFlagDecisionOptions) {
  return useMutation({
    mutationFn: async (payload: Payload) => {
      const validatedPayload = PayloadSchema.parse(payload)
      const apiPayload = transformPayload(validatedPayload)
      return updateDecision(apiPayload)
    },
    ...options,
  })
}

export {
  DecisionSchema,
  // eslint-disable-next-line
  REASONING_VALUES,
  // eslint-disable-next-line
  useSubmitFlagDecision,
  // eslint-disable-next-line
  sendChatMessage,
}
export type { Decision, Payload, ReasoningString }
